import React from 'react';
import { Calendar, BarChart2, PlusCircle, MinusCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  timeUnits
}) => {
  // Обработчик изменения значения
  const handleValueChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      value: newValue
    }));
  };
  
  // Обработчик изменения режима (добавить/вычесть)
  const handleModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      mode
    }));
  };
  
  // Обработчик изменения единицы измерения
  const handleUnitChange = (unit) => {
    setFormData(prev => {
      const newState = {
        ...prev,
        unit
      };
      
      // Если выбрали "рабочие дни", включаем соответствующую опцию
      if (unit === 'workingDays' && !prev.includeWorkingDays) {
        newState.includeWorkingDays = true;
      }
      
      return newState;
    });
  };
  
  // Предопределенные значения для быстрого выбора
  const predefinedValues = [
    { value: '1', label: '1' },
    { value: '7', label: '7' },
    { value: '30', label: '30' },
    { value: '90', label: '90' },
    { value: '180', label: '180' },
    { value: '365', label: '365' }
  ];
  
  // Функция расчета
  const calculate = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
        Быстрый расчет
      </h3>
      
      <div className="space-y-5">
        {/* Тип операции */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Операция</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleModeChange('add')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.mode === 'add' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <PlusCircle className="w-4 h-4 mr-1 text-green-500" />
                Добавить
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('subtract')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.mode === 'subtract' 
                  ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <MinusCircle className="w-4 h-4 mr-1 text-red-500" />
                Вычесть
              </div>
            </button>
          </div>
        </div>
        
        {/* Значение */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="valueSlider" className="text-gray-600 dark:text-gray-400">Количество</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.value}</span>
          </div>
          <input
            type="range"
            id="valueSlider"
            min="1"
            max="365"
            step="1"
            value={formData.value}
            onChange={handleValueChange}
            aria-label="Количество"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-3 gap-1 mt-2">
            {predefinedValues.map(value => (
              <button
                key={value.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, value: value.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.value === value.value 
                    ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Единицы измерения */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Единица измерения</label>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            {timeUnits.slice(0, 4).map(unit => (
              <button
                key={unit.id}
                type="button"
                onClick={() => handleUnitChange(unit.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.unit === unit.id 
                    ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{unit.name}</div>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => handleUnitChange('workingDays')}
            className={`p-2 text-sm rounded-lg border transition-colors w-full
              ${formData.unit === 'workingDays' 
                ? 'bg-cyan-100 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            <div className="font-medium">Рабочие дни</div>
          </button>
        </div>
        
        {/* Учет выходных и праздников */}
        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="quickIncludeWorkingDays"
              checked={formData.includeWorkingDays || formData.unit === 'workingDays'}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                includeWorkingDays: e.target.checked,
                unit: e.target.checked && prev.unit !== 'workingDays' ? 'workingDays' : prev.unit
              }))}
              disabled={formData.unit === 'workingDays'}
              className="form-checkbox"
            />
            <label htmlFor="quickIncludeWorkingDays" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Только рабочие дни
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="quickIncludeHolidays"
              checked={formData.includeHolidays}
              onChange={(e) => setFormData(prev => ({ ...prev, includeHolidays: e.target.checked }))}
              className="form-checkbox"
            />
            <label htmlFor="quickIncludeHolidays" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать праздники
            </label>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculate}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;